import { cva } from "class-variance-authority";
import { JSX } from "react";

const text = cva([], {
  variants: {
    size: {
      review: ["text-base leading-[150%]"],
      body1: ["sm:text-base sm:leading-[150%] text-sm leading-[150%]"],
      loginButtonBody1: ["text-base leading-[150%]"],
      footerLinkListHeader: ["text-base leading-[150%]"],
      body2: ["text-xs sm:text-sm leading-[150%]"],
      body2PackageCard: ["text-[1.25rem] sm:text-sm leading-[150%]"],
      body2PackageCardAddon: ["text-[1.25rem] sm:text-xl leading-[150%]"],
      body2Tooltip: ["text-[0.75rem] sm:text-sm leading-[150%]"],
      body3: ["text-[12px] leading-[150%]"],
      heading6: ["sm:text-xl leading-[150%] "],
      buttonText: ["text-base "],
      link: ["text-md  leading-[1.5rem] underline"],
      brandsBody1: [
        "sm:text-base sm:leading-[150%] text-xs leading-[150%] font-normal",
      ], // goes from body 1 to body 3
      brandsBody2: ["sm:text-sm sm:leading-[150%] text-xs leading-[1.313rem]"], // goes from body 2 to body 3
      // brandsH6: ["sm:text-xl text-[1rem] leading-[150%]"], // h6 goes from 20px to 16px
      tos: [
        "sm:text-[0.625rem] sm:leading-[150%] text-[0.625rem] leading-[1.313rem]",
      ],
      aboutUs40pxText: ["sm:text-[2.5rem] text-2xl leading-[140%]"],
      // locationsH4: ["sm:text-[2rem] text-2xl leading-[150%]"],
      unresponsiveBody1: ["text-[1rem] leading-[150%]"],
      unresponsiveBody2: ["text-[.875rem] leading-[150%]"],
      provincePageHighlightH2: [
        "sm:text-[3.375rem] text-2xl sm:leading-[130%] leading-[140%]",
      ],

      bookingCardServiceType: ["sm:text-[2rem] text-xl leading-[150%]"],
      bookingCardServicePrice: ["sm:text-[1.5rem] text-xl leading-[150%]"],

      ratingsText: ["text-xs sm:text-base leading-[150%]"],
      heroGreetText: ["text-xs sm:text-sm leading-[150%]"],
      packagePrice: [
        "text-[2rem] sm:text-base sm:leading-[150%] text-sm leading-[1.313rem]",
      ],
      fortyToTwentyFour: ["lg:text-[2.5rem] leading-[150%] text-[1.5rem]"],
      ProvenExperienceVariantOne: ["text-base sm:text-xl leading-[140%]"],
    },
    weight: {
      1000: ["font-[1000]"],
      900: ["font-[900]"],
      800: ["font-[800]"],
      700: ["font-[700]"],
      600: ["font-[600]"],
      500: ["font-medium"],
      400: ["font-[400]"],
      300: ["font-[300]"],
    },
    color: {
      white: ["text-white"],
      black: ["text-black"],
      copyGray: ["text-copy-gray"],
      darkGray: ["text-dark-gray"],
      main: ["text-main hover:text-mainHover"],
      mainHover: ["text-mainHover"],
      lightBlue: ["text-[#E3F2FF]"],
      heroGreetColor: ["text-[#6E6E73]"],
      neoGray: ["text-[#9399A6]"],
    },
  },
  defaultVariants: {
    size: "body1",
    weight: 400,
  },
});

export interface TextProps {
  children?: React.ReactNode;
  size?:
    | "unresponsiveBody1"
    | "unresponsiveBody2"
    | "heading6"
    | "loginButtonBody1"
    | "footerLinkListHeader"
    | "body1"
    | "body2"
    | "body3"
    | "link"
    | "buttonText"
    | "review"
    | "brandsBody1"
    | "brandsBody2"
    | "tos"
    // | "locationsH4"
    | "aboutUs40pxText"
    | "provincePageHighlightH2"
    | "bookingCardServiceType"
    | "bookingCardServicePrice"
    | "ratingsText"
    | "heroGreetText"
    | "body2PackageCard"
    | "packagePrice"
    | "body2Tooltip"
    | "body2PackageCardAddon"
    | "fortyToTwentyFour"
    | "ProvenExperienceVariantOne";

  weight?: 300 | 400 | 500 | 600 | 700 | 800 | 900 | 1000;
  color?:
    | "white"
    | "black"
    | "copyGray"
    | "darkGray"
    | "main"
    | "lightBlue"
    | "heroGreetColor"
    | "neoGray"
    | "mainHover";
  as?: keyof JSX.IntrinsicElements;
  className?: string;
  href?: string;
  dangerouslySetInnerHTML?: { __html: string };
  dataTestId?: string;
}

const Typography = ({
  children,
  size,
  weight,
  color,
  as,
  className,
  href,
  dangerouslySetInnerHTML,
  dataTestId,
}: TextProps) => {
  //This will allow for us to provide semantic tags for seo/accessibility
  // When injecting raw HTML, phrasing-content wrappers (<p>, <span>, <li>)
  // cannot legally contain block elements like <p>, <ul>, <div> — which CMS
  // WYSIWYG output almost always produces. Browsers auto-correct the invalid
  // nesting, breaking React hydration (React error #418). Force <div>.
  const requested = as || "p";
  const Component =
    dangerouslySetInnerHTML &&
    (requested === "p" || requested === "span" || requested === "li")
      ? "div"
      : requested;

  const computedClassName = `${text({ size, weight, color })} ${
    className || ""
  }`.trim();

  // Conditionally render based on whether dangerouslySetInnerHTML is provided
  if (dangerouslySetInnerHTML) {
    if (Component === "a") {
      return (
        <a
          data-testid={dataTestId ?? ""}
          href={href}
          className={computedClassName}
          dangerouslySetInnerHTML={dangerouslySetInnerHTML}
        />
      );
    }
    return (
      <Component
        data-testid={dataTestId ?? ""}
        className={computedClassName}
        dangerouslySetInnerHTML={dangerouslySetInnerHTML}
      />
    );
  }

  if (Component === "a") {
    return (
      <a
        data-testid={dataTestId ?? ""}
        href={href}
        className={computedClassName}
      >
        {children}
      </a>
    );
  }

  return (
    <Component data-testid={dataTestId ?? ""} className={computedClassName}>
      {children}
    </Component>
  );
};

export default Typography;
